import Big from 'big.js';

/** The share rates of a payer list or a payment distribution must add up to this. */
export const SHARE_TOTAL = 100;

const AMOUNT_SCALE = 2;
const RATE_SCALE = 2;

/** Half the last kept digit: two values closer than this are the same at the displayed precision. */
const SHARE_TOTAL_TOLERANCE = 0.005;

function toBig(value: number | string | null | undefined): Big | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    try {
        return new Big(value);
    } catch {
        return null;
    }
}

/** A share input's value as a number; blank or unparsable text counts as zero. */
export function shareValueToNumber(value: number | string | null | undefined): number {
    return toBig(value)?.toNumber() ?? 0;
}

export function roundHalfUp(value: number, scale: number): number {
    return new Big(value).round(scale, Big.roundHalfUp).toNumber();
}

/** A share rate for display, always at two decimals ("29.00", "33.33"); blank or unparsable text reads as zero. */
export function formatShareRate(rate: number | string | null | undefined): string {
    return (toBig(rate) ?? new Big(0)).round(RATE_SCALE, Big.roundHalfUp).toFixed(RATE_SCALE);
}

/** A share of net for display: two decimals with the space thousand separator `MoneyDisplay` uses ("6 406.41"). */
export function formatShareAmount(amount: number | string | null | undefined): string {
    const [whole, fraction] = (toBig(amount) ?? new Big(0))
        .round(AMOUNT_SCALE, Big.roundHalfUp)
        .toFixed(AMOUNT_SCALE)
        .split('.');
    return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}.${fraction}`;
}

/** One payer's share of the net: `net × rate / 100`, HALF_UP to cents. */
export function shareAmountFromRate(net: number, rate: number | string | null | undefined): number {
    const rateBig = toBig(rate);
    if (!rateBig) {
        return 0;
    }
    return new Big(net).times(rateBig).div(SHARE_TOTAL).round(AMOUNT_SCALE, Big.roundHalfUp).toNumber();
}

/** The rate a share of the net stands for: `amount / net × 100`, HALF_UP to two decimals; zero when there is no net. */
export function shareRateFromAmount(net: number, amount: number | string | null | undefined): number {
    const amountBig = toBig(amount);
    if (!amountBig || net === 0) {
        return 0;
    }
    return amountBig.div(net).times(SHARE_TOTAL).round(RATE_SCALE, Big.roundHalfUp).toNumber();
}

/**
 * The share amounts of every rate, HALF_UP to cents, with the rounding remainder on the first entry (the main
 * payer, or the first invoice of a group) so the amounts add up to the net exactly. The remainder is left out
 * while the rates do not add up to 100, so a list being edited still shows amounts that follow its rates.
 */
export function shareAmountsFromRates(net: number, rates: (number | string | null | undefined)[]): number[] {
    if (rates.length === 0) {
        return [];
    }
    const amounts = rates.map((rate) => new Big(shareAmountFromRate(net, rate)));
    const total = sumShareRates(rates);
    if (Math.abs(total - SHARE_TOTAL) >= SHARE_TOTAL_TOLERANCE) {
        return amounts.map((amount) => amount.toNumber());
    }
    const allocated = amounts.reduce((sum, amount) => sum.plus(amount), new Big(0));
    const remainder = new Big(net).minus(allocated);
    return amounts.map((amount, index) => (index === 0 ? amount.plus(remainder) : amount).toNumber());
}

export function sumShareRates(rates: (number | string | null | undefined)[]): number {
    return rates
        .reduce((sum, rate) => sum.plus(toBig(rate) ?? 0), new Big(0))
        .round(RATE_SCALE, Big.roundHalfUp)
        .toNumber();
}

export function sumShareAmounts(amounts: (number | string | null | undefined)[]): number {
    return amounts
        .reduce((sum, amount) => sum.plus(toBig(amount) ?? 0), new Big(0))
        .round(AMOUNT_SCALE, Big.roundHalfUp)
        .toNumber();
}

/** Whether the rates add up to 100 at the displayed precision. */
export function isShareTotalComplete(rates: (number | string | null | undefined)[]): boolean {
    return Math.abs(sumShareRates(rates) - SHARE_TOTAL) < SHARE_TOTAL_TOLERANCE;
}

/**
 * The rate a payer added to the list starts with: whatever the others leave of the 100, so a payer added
 * while the others already total 100 starts at 0.
 */
export function initialShareRateForNewPayer(rates: (number | string | null | undefined)[]): number {
    return Math.max(0, roundHalfUp(SHARE_TOTAL - sumShareRates(rates), RATE_SCALE));
}

export function isShareRateInRange(rate: number | string | null | undefined): boolean {
    const rateBig = toBig(rate);
    return rateBig !== null && rateBig.gte(0) && rateBig.lte(SHARE_TOTAL);
}

/** Whether a share rate is given and lies outside 0..100; a blank or unfinished one is not out of range yet. */
export function isShareRateOutOfRange(rate: number | string | null | undefined): boolean {
    const rateBig = toBig(rate);
    return rateBig !== null && (rateBig.lt(0) || rateBig.gt(SHARE_TOTAL));
}
